import {
  parseInTimezone,
  addMinutes,
  formatTime as datetimeFormatTime,
  formatDate as datetimeFormatDate,
  strftimeToLuxonFormat
} from '@calendly/datetime'

import { i18n } from 'locales'

export const formatDate = (time, format, locale) => {
  const luxonFormat = strftimeToLuxonFormat(format || i18n.t('time.formats.date_full'))
  const currentLocale = locale || i18n.locale
  return datetimeFormatDate(time, { format: luxonFormat, locale: currentLocale })
}

export const buildSpot = (startTime, duration, timezone) => {
  const start = parseInTimezone(startTime, timezone)
  return {
    start_time: start,
    end_time: addMinutes(start, duration)
  }
}

export const formatEventTime = (spot, notation, timezone) => {
  const startTime = timezone ? parseInTimezone(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseInTimezone(spot.end_time, timezone) : spot.end_time
  const startDate = formatDate(startTime)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${startFormatted} - ${endFormatted},\n ${startDate}`
}

export const formatEventTimeToDisplay = (spot, notation, timezone) => {
  const startTime = timezone ? parseInTimezone(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseInTimezone(spot.end_time, timezone) : spot.end_time
  return `${datetimeFormatTime(startTime, { notation })} - ${datetimeFormatTime(endTime, { notation })}`
}

export const formatEnterDetailsSpotHeadingLine = (spot, notation, timezone, locale) => {
  const startTime = timezone ? parseInTimezone(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseInTimezone(spot.end_time, timezone) : spot.end_time
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${datePart}, ${startFormatted} – ${endFormatted}`
}

const formatShortTimezoneAbbrev = (dt, ianaTz, locale) => {
  if (!dt?.isValid || !ianaTz) return ''
  const loc = String(locale || 'en').replace('_', '-')
  try {
    const parts = new Intl.DateTimeFormat(loc, {
      timeZone: ianaTz,
      timeZoneName: 'short'
    }).formatToParts(dt.toJSDate())
    return parts.find((p) => p.type === 'timeZoneName')?.value?.trim() || ''
  } catch {
    return typeof dt.offsetNameShort === 'string' ? dt.offsetNameShort : ''
  }
}

/** Matches reschedule Enter Details screenshot: "Weekday, Mon D, YYYY, h:mm am — h:mm am (TZ)" */
export const formatRescheduleEnterDetailsRangeLine = (source, notation, timezoneId, locale) => {
  if (!source?.start_time || !source?.end_time || !timezoneId) return ''
  const startTime = parseInTimezone(source.start_time, timezoneId)
  const endTime = parseInTimezone(source.end_time, timezoneId)
  if (!startTime?.isValid || !endTime?.isValid) return ''
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  const tzAbbrev = formatShortTimezoneAbbrev(startTime, timezoneId, locale)
  const emDash = '\u2014'
  const tzPart = tzAbbrev ? ` (${tzAbbrev})` : ''
  return `${datePart}, ${startFormatted} ${emDash} ${endFormatted}${tzPart}`
}
